import { ChangeEvent, useRef, useState } from 'react'
import { Switch } from '@progress/kendo-react-inputs'
import { FaCheckCircle, FaClipboardCheck, FaRegCircle, FaTrash, FaUpload } from 'react-icons/fa'
import TabSectionHeader from './TabSectionHeader'
import {
  JobNotifiableWork,
  JobNotifiableWorkDocument,
  JobNotifiableWorkFile,
  setNotifiableWorkDocumentCompleted,
  uploadNotifiableWorkFiles,
} from '../../../data/notifiableWorkData'
import { deleteDocument, getDocument } from '../../../data/fileData'
import { useToast } from '../../../contexts/ToastContext'
import { getApiErrorDetails } from '../../../helpers/apiError'

interface JobNotifiableWorksProps {
  jobId: number
  /** The notifiable work picked in the rail. */
  work: JobNotifiableWork | null
  lockEdit: boolean
  /** Called after anything is actioned, so the rail's badges catch up. */
  onChanged: () => Promise<void> | void
}

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * One notifiable work on a job: the documents it needs, in the order an
 * administrator set. A file upload document is done once it has at least one
 * file, and can hold as many as needed; any other is ticked off with a switch. Required ones are highlighted, and
 * only they count towards the work's badge in the rail.
 */
const JobNotifiableWorks = ({ jobId, work, lockEdit, onChanged }: JobNotifiableWorksProps) => {
  const { showToast } = useToast()
  const [busyId, setBusyId] = useState<number | null>(null)

  // One hidden file input, pointed at whichever document's Upload was pressed.
  const fileInput = useRef<HTMLInputElement>(null)
  const uploadFor = useRef<JobNotifiableWorkDocument | null>(null)

  if (!work) {
    return null
  }

  const requiredDone = work.requiredCount - work.outstanding
  const optionalCount = work.documents.length - work.requiredCount

  const description =
    work.documents.length === 0
      ? 'No documents are needed for this notifiable work.'
      : work.requiredCount === 0
        ? 'None of these documents are required.'
        : `${requiredDone} of ${work.requiredCount} required document${work.requiredCount === 1 ? '' : 's'} done` +
          (optionalCount > 0 ? `, plus ${optionalCount} optional` : '')

  const run = async (document: JobNotifiableWorkDocument, action: () => Promise<unknown>, success: string, failure: string) => {
    setBusyId(document.notifiableWorkDocumentId)
    try {
      await action()
      showToast(success, 'success')
      await onChanged()
    } catch (error) {
      showToast(getApiErrorDetails(error, failure).message, 'error')
    } finally {
      setBusyId(null)
    }
  }

  const pickFile = (document: JobNotifiableWorkDocument) => {
    uploadFor.current = document
    fileInput.current?.click()
  }

  const onFilesChosen = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const document = uploadFor.current
    e.target.value = ''
    if (files.length === 0 || !document) return
    run(
      document,
      () => uploadNotifiableWorkFiles(jobId, document.notifiableWorkDocumentId, files),
      files.length === 1 ? `${document.name}: file uploaded` : `${document.name}: ${files.length} files uploaded`,
      files.length === 1 ? "The file couldn't be uploaded." : "The files couldn't be uploaded."
    )
  }

  const removeFile = (document: JobNotifiableWorkDocument, file: JobNotifiableWorkFile) => {
    if (!window.confirm(`Remove ${file.fileName} from ${document.name}?`)) return
    run(document, () => deleteDocument(file.documentId), 'File removed', "The file couldn't be removed.")
  }

  const setCompleted = (document: JobNotifiableWorkDocument, completed: boolean) =>
    run(
      document,
      () => setNotifiableWorkDocumentCompleted(jobId, document.notifiableWorkDocumentId, completed),
      completed ? `${document.name} marked as completed` : `${document.name} marked as not completed`,
      "It couldn't be updated."
    )

  const openFile = (file: JobNotifiableWorkFile) => {
    getDocument(file.documentId, file.fileName).catch((error) =>
      showToast(getApiErrorDetails(error, "The file couldn't be opened.").message, 'error')
    )
  }

  return (
    <div className='jobnw'>
      <TabSectionHeader icon={<FaClipboardCheck />} title={work.name} description={description} />

      {work.documents.length === 0 ? (
        <p className='jobnw__empty'>
          An administrator can add the documents it needs under Management, Notifiable Works.
        </p>
      ) : (
        <ul className='jobnw__list'>
          {work.documents.map((document) => {
            const busy = busyId === document.notifiableWorkDocumentId
            const rowClass = [
              'jobnw__row',
              document.isRequired ? 'jobnw__row--required' : '',
              document.isRequired && !document.actioned ? 'jobnw__row--outstanding' : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <li key={document.notifiableWorkDocumentId} className={rowClass}>
                <span className={`jobnw__status${document.actioned ? ' jobnw__status--done' : ''}`} aria-hidden='true'>
                  {document.actioned ? <FaCheckCircle /> : <FaRegCircle />}
                </span>

                <div className='jobnw__text'>
                  <span className='jobnw__name'>
                    {document.name}
                    {document.isRequired ? (
                      <span className='jobnw__tag jobnw__tag--required'>Required</span>
                    ) : (
                      <span className='jobnw__tag'>Optional</span>
                    )}
                  </span>

                  {document.isFileUpload ? (
                    document.files.length > 0 ? (
                      <ul className='jobnw__files'>
                        {document.files.map((file) => (
                          <li key={file.documentId} className='jobnw__file-item'>
                            <button type='button' className='jobnw__file' onClick={() => openFile(file)}>
                              {file.fileName}
                            </button>
                            {!lockEdit && (
                              <button
                                type='button'
                                className='jobnw__remove jobnw__remove--small'
                                title='Remove file'
                                aria-label={`Remove ${file.fileName} from ${document.name}`}
                                onClick={() => removeFile(document, file)}
                                disabled={busyId !== null}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className='jobnw__missing'>No file uploaded</span>
                    )
                  ) : document.completed ? (
                    <span className='jobnw__done-by'>
                      Completed {formatDateTime(document.completed.completedDate)}
                      {document.completed.completedBy ? ` by ${document.completed.completedBy}` : ''}
                    </span>
                  ) : (
                    <span className='jobnw__missing'>Not completed</span>
                  )}
                </div>

                {document.isFileUpload ? (
                  !lockEdit && (
                    <div className='jobnw__actions'>
                      <button
                        type='button'
                        className='jobsection__action-btn'
                        onClick={() => pickFile(document)}
                        disabled={busyId !== null}
                      >
                        <FaUpload /> {busy ? 'Working…' : document.files.length > 0 ? 'Add more' : 'Upload'}
                      </button>
                    </div>
                  )
                ) : (
                  <label className='jobnw__switch'>
                    <span>Completed</span>
                    <Switch
                      checked={!!document.completed}
                      disabled={lockEdit || busyId !== null}
                      onChange={(e) => setCompleted(document, e.value)}
                      ariaLabel={`${document.name} completed`}
                    />
                  </label>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <input ref={fileInput} type='file' multiple hidden onChange={onFilesChosen} />
    </div>
  )
}

export default JobNotifiableWorks
